/**
 * How stale a cached fix may be when it becomes a Small Group's pin.
 *
 * `docs/group-create-latency-plan.md` §3. Group create is the one place a fix
 * gets FROZEN: the server pins the group's location from it for the group's
 * whole life, and that pin gates who may join. So the allowance is not the
 * heartbeat's blanket one — it depends on how narrow a rule the pin is about
 * to anchor.
 *
 * Two tiers, not a formula. The server prices staleness at 1.4 m/s
 * (`presence-range.ts`), so a fix as old as `HEARTBEAT_CACHED_FIX_MAX_AGE_MS`
 * (120s) carries ~168m of possible drift: noise against a kilometre-plus
 * radius, arguable against the 500m floor. The narrow tier therefore keeps
 * today's behaviour — a live read — and everything at or above a kilometre
 * accepts what the presence system already tolerates for discovery, which
 * lets a stored coordinate reach twice this age between heartbeats.
 *
 * Deliberately the heartbeat's NUMBER rather than a new one: a second
 * freshness ceiling would be one more pair able to drift apart, and the
 * argument for 120s here IS the argument that already justified it there.
 * A LOCAL mirror rather than an import, the same trade `location.ts` makes
 * with `LAST_KNOWN_MAX_ACCURACY_M`: this module must stay import-free so the
 * root `node --test` suite can load it (the `@/` alias is Metro's, and a
 * `.ts`-extension import is refused app-side — TS5097, see `telemetry.ts`).
 * Unlike that mirror, the equality is PINNED by
 * `group-create-coords.test.ts`, which imports both modules and fails the
 * moment they part company.
 */

/**
 * Mirror of `HEARTBEAT_CACHED_FIX_MAX_AGE_MS` (`state/presence-heartbeat.ts`).
 * Change that one first; the test will bring you here.
 */
export const GROUP_CREATE_CACHED_FIX_MAX_AGE_MS = 120_000;

/** Below this radius the pin is narrow enough that only a live fix will do. */
export const GROUP_CREATE_LIVE_FIX_BELOW_RADIUS_M = 1000;

/**
 * The `acceptCachedWithinMs` for both group-create reads: the warm at modal
 * open and the read at Create. The same bound at both ends is what makes the
 * warm self-deduplicating — a cache the submit read would accept is a cache
 * the warm declines to refresh.
 *
 * Zero disables the cache entirely (`cachedFixIsFreshEnough` refuses `<= 0`),
 * which is the pre-existing behaviour this module narrows rather than a
 * special case of its own.
 */
export function groupCreateCacheBoundMs(radiusM: number): number {
  return radiusM < GROUP_CREATE_LIVE_FIX_BELOW_RADIUS_M
    ? 0
    : GROUP_CREATE_CACHED_FIX_MAX_AGE_MS;
}
